import React, { useState, useEffect, useCallback, useMemo } from "react";
import { CheckCircle2, XCircle, AlertTriangle, Eye, MailCheck, MailX, Loader2, RefreshCw, Filter, Send } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "fad_admin_token";

const STATUS_PILL = {
  sent:        { label: "Sent",       icon: CheckCircle2, color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" },
  delivered:   { label: "Delivered",  icon: MailCheck,    color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  opened:      { label: "Opened",     icon: Eye,          color: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  clicked:     { label: "Clicked",    icon: Eye,          color: "bg-sky-500/20 text-sky-200 border-sky-500/50" },
  failed:      { label: "Failed",     icon: XCircle,      color: "bg-rose-500/15 text-rose-300 border-rose-500/40" },
  bounced:     { label: "Bounced",    icon: MailX,        color: "bg-rose-500/15 text-rose-300 border-rose-500/40" },
  complained:  { label: "Spam",       icon: AlertTriangle,color: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  delayed:     { label: "Delayed",    icon: Loader2,      color: "bg-amber-500/10 text-amber-300 border-amber-500/30" },
};

const KINDS = [
  "all",
  "contact_admin", "contact_customer_reply",
  "inquiry_admin", "inquiry_customer_reply",
  "wire_customer", "wire_admin",
  "paid_customer", "paid_admin", "order_customer", "order_admin",
  "shipping_customer",
  "bounce_admin_alert", "email_failure_digest",
  "webhook_health_alert",
];

function StatusPill({ status }) {
  const cfg = STATUS_PILL[status] || { label: status || "—", icon: AlertTriangle, color: "bg-neutral-700/30 text-neutral-300 border-neutral-600/40" };
  const Icon = cfg.icon;
  return (
    <span data-testid={`status-pill-${status || "none"}`} className={`inline-flex items-center gap-1.5 border px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wider uppercase ${cfg.color}`}>
      <Icon size={11} strokeWidth={2} />
      {cfg.label}
    </span>
  );
}

function StatCard({ label, value, sub, tone = "default" }) {
  const toneStyle = {
    default: "border-neutral-800",
    good:    "border-emerald-500/30",
    bad:     "border-rose-500/40",
    warn:    "border-amber-500/40",
  }[tone];
  return (
    <div data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`} className={`bg-[#0a0a0a] border ${toneStyle} rounded-lg p-4`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-500 mb-1.5">{label}</div>
      <div className="font-display text-3xl text-white">{value}</div>
      {sub && <div className="text-xs text-neutral-500 mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminEmails() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [tokenInput, setTokenInput] = useState("");
  const [health, setHealth] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [kindFilter, setKindFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [digestSending, setDigestSending] = useState(false);
  const [digestMsg, setDigestMsg] = useState("");

  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setErr("");
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (kindFilter !== "all") params.set("kind", kindFilter);
      if (statusFilter !== "all") params.set("status", statusFilter);
      const [healthRes, logRes] = await Promise.all([
        fetch(`${API}/api/admin/email-health`, { headers: { "X-Admin-Token": token } }),
        fetch(`${API}/api/admin/email-log?${params.toString()}`, { headers: { "X-Admin-Token": token } }),
      ]);
      if (healthRes.status === 401 || logRes.status === 401) {
        setErr("Wrong admin token — clear and try again.");
        setHealth(null);
        setRows([]);
        return;
      }
      const h = await healthRes.json();
      const l = await logRes.json();
      setHealth(h);
      setRows(l.items || []);
    } catch (e) {
      setErr(`Network error: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [token, kindFilter, statusFilter]);

  useEffect(() => {
    if (token) fetchAll();
  }, [fetchAll, token]);

  useEffect(() => {
    if (!autoRefresh || !token) return;
    const interval = setInterval(fetchAll, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchAll, token]);

  const handleSignIn = (e) => {
    e.preventDefault();
    const trimmed = tokenInput.trim();
    if (!trimmed) return;
    localStorage.setItem(TOKEN_KEY, trimmed);
    setToken(trimmed);
  };

  const handleSignOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setHealth(null);
    setRows([]);
    setErr("");
  };

  const handleRunDigest = async () => {
    setDigestSending(true);
    setDigestMsg("");
    try {
      const res = await fetch(`${API}/api/admin/email-digest/run`, { method: "POST", headers: { "X-Admin-Token": token } });
      const data = await res.json();
      if (data.digest_sent) setDigestMsg(`Digest sent — ${data.items} item(s) flagged in the last 24h.`);
      else setDigestMsg("Nothing to report — last 24h had no failures or bounces. (No email sent — that's healthy.)");
      fetchAll();
    } catch (e) {
      setDigestMsg(`Failed: ${e.message}`);
    } finally {
      setDigestSending(false);
    }
  };

  const filteredRows = useMemo(() => rows, [rows]);

  // -- Sign-in screen --------------------------------------------------------
  if (!token) {
    return (
      <div data-testid="admin-emails-signin" className="min-h-screen bg-black text-white flex items-center justify-center p-6">
        <form onSubmit={handleSignIn} className="bg-[#0a0a0a] border border-neutral-800 rounded-lg p-8 w-full max-w-md">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#FF6B00] font-bold mb-3">Admin · email console</div>
          <h1 className="font-display text-2xl mb-6">Enter admin token</h1>
          <p className="text-sm text-neutral-400 mb-5 leading-relaxed">
            The token is the value of <code className="text-[#FF6B00] bg-neutral-900 px-1.5 py-0.5 rounded text-xs">WC_WEBHOOK_SECRET</code> in your <code className="text-[#FF6B00] bg-neutral-900 px-1.5 py-0.5 rounded text-xs">backend/.env</code>.
          </p>
          <input
            data-testid="admin-token-input"
            type="password"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="fortune_wc_webhook_..."
            className="w-full bg-black border border-neutral-700 focus:border-[#FF6B00] outline-none px-4 py-3 rounded text-sm text-white placeholder:text-neutral-600 mb-4"
            autoFocus
          />
          <button
            data-testid="admin-signin-btn"
            type="submit"
            className="w-full bg-[#FF6B00] hover:bg-[#ff7d1f] active:bg-[#e55a00] text-black font-bold tracking-wider text-sm py-3 rounded transition"
          >
            UNLOCK
          </button>
          <p className="text-[11px] text-neutral-600 mt-5 leading-relaxed">
            Token is stored only in your browser&apos;s localStorage. Sign out clears it. This page is hidden from search engines.
          </p>
        </form>
      </div>
    );
  }

  // -- Dashboard -------------------------------------------------------------
  const c = health?.counts || {};
  const deliveredPct = c.sent ? Math.round(((c.delivered || 0) / c.sent) * 100) : 0;
  const openedPct = c.delivered ? Math.round(((c.opened || 0) / c.delivered) * 100) : 0;

  return (
    <div data-testid="admin-emails-page" className="min-h-screen bg-black text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#FF6B00] font-bold mb-1">Admin · email console</div>
            <h1 className="font-display text-4xl tracking-tight">Email health</h1>
            <p className="text-sm text-neutral-500 mt-1">Live view of every transactional email — last 24h.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="refresh-btn"
              onClick={fetchAll}
              disabled={loading}
              className="inline-flex items-center gap-2 border border-neutral-700 hover:border-[#FF6B00] hover:text-[#FF6B00] text-neutral-300 text-xs tracking-wider uppercase px-3 py-2 rounded transition disabled:opacity-50"
            >
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <label className="inline-flex items-center gap-2 text-xs text-neutral-400 cursor-pointer">
              <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} className="accent-[#FF6B00]" />
              Auto 30s
            </label>
            <button
              data-testid="signout-btn"
              onClick={handleSignOut}
              className="text-xs tracking-wider uppercase text-neutral-500 hover:text-rose-400 px-2 py-2 transition"
            >
              Sign out
            </button>
          </div>
        </div>

        {err && (
          <div data-testid="error-banner" className="bg-rose-900/30 border border-rose-500/40 text-rose-300 px-4 py-3 rounded mb-6 text-sm">
            {err}
          </div>
        )}

        {/* Health summary */}
        {health && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <StatCard
                label="Status"
                value={health.healthy ? "Healthy" : "Attention"}
                sub={health.healthy ? "All systems green" : `${c.failed || 0} failure(s) in last 24h`}
                tone={health.healthy ? "good" : "bad"}
              />
              <StatCard label="Sent" value={c.sent || 0} sub={`${c.total_attempts || 0} attempts`} tone="default" />
              <StatCard label="Delivered" value={c.delivered || 0} sub={`${deliveredPct}% of sent`} tone={c.delivered > 0 ? "good" : "default"} />
              <StatCard label="Opened" value={c.opened || 0} sub={`${openedPct}% of delivered`} tone={c.opened > 0 ? "good" : "default"} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
              <StatCard label="Failed" value={c.failed || 0} tone={c.failed > 0 ? "bad" : "good"} />
              <StatCard label="Bounced" value={c.bounced || 0} tone={c.bounced > 0 ? "warn" : "good"} />
              <StatCard label="Spam complaints" value={c.complained || 0} tone={c.complained > 0 ? "bad" : "good"} />
              <StatCard
                label="Last success"
                value={health.last_success_at ? new Date(health.last_success_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                sub={health.last_success_at ? new Date(health.last_success_at).toLocaleDateString() : "No sends yet"}
                tone="default"
              />
            </div>

            {/* Config banner */}
            <div className="bg-[#0a0a0a] border border-neutral-800 rounded-lg p-4 mb-8 text-xs text-neutral-400 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-6 flex-wrap">
                <span className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${health.resend_configured ? "bg-emerald-400" : "bg-rose-400"}`} />
                  Resend API: {health.resend_configured ? "configured" : "NOT configured"}
                </span>
                <span className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${health.resend_webhook_secret_configured ? "bg-emerald-400" : "bg-amber-400"}`} />
                  Webhook signing: {health.resend_webhook_secret_configured ? "verified" : "open (set RESEND_WEBHOOK_SECRET to harden)"}
                </span>
                <span className="flex items-center gap-2">
                  Admin recipients: <span className="text-neutral-200">{(health.admin_recipients || []).join(", ") || "—"}</span>
                </span>
              </div>
              <button
                data-testid="run-digest-btn"
                onClick={handleRunDigest}
                disabled={digestSending}
                className="inline-flex items-center gap-2 border border-neutral-700 hover:border-[#FF6B00] hover:text-[#FF6B00] text-neutral-300 text-xs tracking-wider uppercase px-3 py-2 rounded transition disabled:opacity-50"
              >
                {digestSending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                Run digest now
              </button>
            </div>
            {digestMsg && (
              <div data-testid="digest-msg" className="bg-neutral-900/50 border border-neutral-800 text-neutral-300 px-4 py-3 rounded mb-6 text-sm">{digestMsg}</div>
            )}
          </>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <Filter size={14} className="text-neutral-500" />
          <select
            data-testid="kind-filter"
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value)}
            className="bg-black border border-neutral-700 focus:border-[#FF6B00] outline-none px-3 py-2 rounded text-xs text-neutral-200"
          >
            {KINDS.map((k) => <option key={k} value={k}>{k === "all" ? "All kinds" : k}</option>)}
          </select>
          <select
            data-testid="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-black border border-neutral-700 focus:border-[#FF6B00] outline-none px-3 py-2 rounded text-xs text-neutral-200"
          >
            <option value="all">All statuses</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
          </select>
          <span className="text-xs text-neutral-500 ml-auto">{filteredRows.length} row{filteredRows.length !== 1 ? "s" : ""}</span>
        </div>

        {/* Table */}
        <div className="bg-[#0a0a0a] border border-neutral-800 rounded-lg overflow-x-auto">
          <table className="w-full text-sm" data-testid="email-log-table">
            <thead className="bg-[#111] border-b border-neutral-800">
              <tr>
                <th className="text-left px-4 py-3 text-[10px] tracking-[0.15em] uppercase text-neutral-500 font-semibold">When</th>
                <th className="text-left px-4 py-3 text-[10px] tracking-[0.15em] uppercase text-neutral-500 font-semibold">Kind</th>
                <th className="text-left px-4 py-3 text-[10px] tracking-[0.15em] uppercase text-neutral-500 font-semibold">To</th>
                <th className="text-left px-4 py-3 text-[10px] tracking-[0.15em] uppercase text-neutral-500 font-semibold">Send</th>
                <th className="text-left px-4 py-3 text-[10px] tracking-[0.15em] uppercase text-neutral-500 font-semibold">Delivery</th>
                <th className="text-left px-4 py-3 text-[10px] tracking-[0.15em] uppercase text-neutral-500 font-semibold">Subject / detail</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 && (
                <tr><td colSpan={6} className="text-center text-neutral-500 py-10">{loading ? "Loading…" : "No emails in this view yet."}</td></tr>
              )}
              {filteredRows.map((r, i) => (
                <tr key={`${r.resend_id}-${i}`} className="border-b border-neutral-900 hover:bg-neutral-900/50" data-testid={`email-row-${i}`}>
                  <td className="px-4 py-3 text-xs text-neutral-400 whitespace-nowrap">
                    {r.sent_at ? new Date(r.sent_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" }) : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs"><code className="text-[#FF6B00]">{r.kind}</code></td>
                  <td className="px-4 py-3 text-xs text-neutral-200">{(r.to || []).join(", ")}</td>
                  <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                  <td className="px-4 py-3"><StatusPill status={r.delivery_status} /></td>
                  <td className="px-4 py-3 text-xs text-neutral-400 max-w-md truncate">
                    {r.error
                      ? <span className="text-rose-300">{r.error}</span>
                      : r.bounce_reason
                        ? <span className="text-amber-300">↳ {r.bounce_reason}</span>
                        : r.subject}
                    {r.ref && <span className="ml-2 text-neutral-600">· {r.ref}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-[11px] text-neutral-600 mt-6 leading-relaxed">
          Auto-refreshes every 30 seconds. Send statuses: <strong>sent</strong> = handed to Resend. Delivery statuses come from the Resend webhook — <strong>delivered</strong> means the recipient&apos;s mail server accepted it; <strong>opened</strong> means they opened it; <strong>bounced</strong> means it failed at the recipient end (typo, full mailbox); <strong>spam</strong> means they marked it as junk. Bounces and spam complaints also trigger an admin alert email automatically.
        </p>

        <VersionBadge />
      </div>
    </div>
  );
}

function VersionBadge() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/version`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setInfo)
      .catch(() => setInfo({ error: true }));
  }, []);
  if (!info) return null;
  if (info.error || !info.booted_at) {
    return (
      <div data-testid="version-badge" className="mt-4 inline-flex items-center gap-2 text-[10px] tracking-[0.15em] uppercase text-rose-400 border border-rose-500/40 bg-rose-900/20 px-2 py-1 rounded">
        <span className="w-1.5 h-1.5 rounded-full bg-rose-400" /> /api/version not available — production may be running old code. Re-deploy from Emergent.
      </div>
    );
  }
  const booted = new Date(info.booted_at);
  const ageMs = Date.now() - booted.getTime();
  const ageHours = Math.floor(ageMs / 36e5);
  const ageDays = Math.floor(ageHours / 24);
  const ageLabel = ageDays > 0 ? `${ageDays}d ago` : ageHours > 0 ? `${ageHours}h ago` : "just now";
  const stale = ageDays > 14;
  return (
    <div data-testid="version-badge" className={`mt-4 inline-flex items-center gap-2 text-[10px] tracking-[0.15em] uppercase border px-2 py-1 rounded ${stale ? "text-amber-300 border-amber-500/40 bg-amber-900/20" : "text-emerald-400 border-emerald-500/30 bg-emerald-900/10"}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${stale ? "bg-amber-400" : "bg-emerald-400"}`} />
      Backend deployed {ageLabel} · {booted.toLocaleString()}
      {stale && <span className="ml-2">(consider re-deploying)</span>}
    </div>
  );
}
